import { Button } from "@mantine/core";
import { queryOptions, useQueries, useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { ReactNode } from "react";
import type { Tables } from "@how/database";
import { getModule } from "@how/entitlements";
import { MODULE_KEYS, type ModuleKey, type PermissionKey, type RoleKey } from "@how/types";
import { ErrorState, LoadingState, PermissionDenied } from "@how/ui";
import { isAuthorizationError } from "./query-client";
import { useSession, useSupabase } from "./session";
import type { AppSupabaseClient } from "./supabase";
import { membershipQuery, useTenant } from "./tenant";

export interface PermissionQuery {
  readonly permission: PermissionKey;
  /** Extra module the action needs beyond the permission's own module. */
  readonly module?: ModuleKey;
  /** The action writes; a read-only support session then does not satisfy it. */
  readonly write?: boolean;
}

export function useScope() {
  const session = useSession();
  const tenant = useTenant();
  return {
    userId: session.status === "signed_in" ? session.session.user.id : null,
    tenantId: tenant.status === "resolved" ? tenant.tenant.tenantId : null,
  };
}

/**
 * Asks the database whether the caller may do something in the current tenant. `authorize_check` runs the full
 * formula (membership, role, module entitlement, tenant MFA rule, support session), so the client never re-implements
 * it; the server still enforces it on every read and RPC.
 */
export function permissionQuery(
  supabase: AppSupabaseClient,
  tenantId: string,
  userId: string,
  q: PermissionQuery,
) {
  return queryOptions({
    queryKey: ["authorize", tenantId, userId, q.permission, q.module ?? null, q.write ?? false],
    queryFn: async (): Promise<boolean> => {
      const { data, error } = await supabase.rpc("authorize_check", {
        p_tenant_id: tenantId,
        p_permission_key: q.permission,
        ...(q.module === undefined ? {} : { p_module_key: q.module }),
        ...(q.write === undefined ? {} : { p_write: q.write }),
      });
      if (error) throw error;
      return data;
    },
  });
}

export function usePermission(
  permission: PermissionKey,
  options: Omit<PermissionQuery, "permission"> = {},
) {
  const supabase = useSupabase();
  const { userId, tenantId } = useScope();
  return useQuery({
    ...permissionQuery(supabase, tenantId ?? "", userId ?? "", { permission, ...options }),
    enabled: userId !== null && tenantId !== null,
  });
}

export interface PermissionsResult {
  readonly isPending: boolean;
  /** Permission keys the caller holds; absent keys are denied or still loading. */
  readonly allowed: ReadonlySet<PermissionKey>;
}

/** Several permission checks at once, for navigation filtering. */
export function usePermissions(queries: readonly PermissionQuery[]): PermissionsResult {
  const supabase = useSupabase();
  const { userId, tenantId } = useScope();
  const results = useQueries({
    queries: queries.map((q) => ({
      ...permissionQuery(supabase, tenantId ?? "", userId ?? "", q),
      enabled: userId !== null && tenantId !== null,
    })),
  });
  const allowed = new Set<PermissionKey>();
  results.forEach((r, i) => {
    const q = queries[i];
    if (q !== undefined && r.data === true) allowed.add(q.permission);
  });
  return { isPending: results.some((r) => r.isPending), allowed };
}

export interface RequirePermissionProps extends PermissionQuery {
  readonly children: ReactNode;
}

/** Renders children only when `authorize_check` allows the action; otherwise PermissionDenied. */
export function RequirePermission({ permission, module, write, children }: RequirePermissionProps) {
  const query = usePermission(permission, {
    ...(module === undefined ? {} : { module }),
    ...(write === undefined ? {} : { write }),
  });
  if (query.isPending) return <LoadingState label="Checking your access" />;
  if (query.isError) {
    return isAuthorizationError(query.error) ? (
      <PermissionDenied />
    ) : (
      <ErrorState
        message="We could not check your access."
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }
  return query.data ? <>{children}</> : <PermissionDenied />;
}

type EntitlementRow = Pick<
  Tables<"tenant_module_entitlements">,
  "module_key" | "status" | "starts_at" | "ends_at"
>;

/**
 * Modules usable in the current tenant, mirroring private.has_module(): base modules always, planned modules never,
 * everything else only inside an active entitlement window. Navigation convenience; the server decides.
 */
export function entitlementsQuery(supabase: AppSupabaseClient, tenantId: string) {
  return queryOptions({
    queryKey: ["entitlements", tenantId],
    queryFn: async (): Promise<ReadonlySet<ModuleKey>> => {
      const { data, error } = await supabase
        .from("tenant_module_entitlements")
        .select("module_key, status, starts_at, ends_at")
        .eq("tenant_id", tenantId);
      if (error) throw error;
      const now = Date.now();
      const active = new Set<string>(
        (data as EntitlementRow[])
          .filter(
            (row) =>
              row.status === "active" &&
              Date.parse(row.starts_at) <= now &&
              (row.ends_at === null || Date.parse(row.ends_at) > now),
          )
          .map((row) => row.module_key),
      );
      const enabled = new Set<ModuleKey>();
      for (const key of MODULE_KEYS) {
        const def = getModule(key);
        if (def.isBase) enabled.add(key);
        else if (def.status !== "planned" && active.has(key)) enabled.add(key);
      }
      return enabled;
    },
  });
}

export function useEntitlements() {
  const supabase = useSupabase();
  const { tenantId } = useScope();
  return useQuery({ ...entitlementsQuery(supabase, tenantId ?? ""), enabled: tenantId !== null });
}

/** Renders children only when the module is usable in this tenant. */
export function RequireModule({ module, children }: { module: ModuleKey; children: ReactNode }) {
  const entitlements = useEntitlements();
  if (entitlements.isPending) return <LoadingState label="Checking your church's modules" />;
  if (entitlements.isError) {
    return isAuthorizationError(entitlements.error) ? (
      <PermissionDenied />
    ) : (
      <ErrorState
        message="We could not check your church's modules."
        onRetry={() => {
          void entitlements.refetch();
        }}
      />
    );
  }
  if (!entitlements.data.has(module)) {
    const def = getModule(module);
    return (
      <PermissionDenied
        title={`${def.displayName} is not enabled`}
        message={
          def.status === "planned"
            ? `${def.displayName} is coming in a later release.`
            : `Ask a church administrator to enable ${def.displayName}.`
        }
      />
    );
  }
  return <>{children}</>;
}

export interface MyRoles {
  readonly keys: readonly RoleKey[];
  readonly isPrivileged: boolean;
}

/**
 * The caller's roles in the current tenant, used for the tenant MFA rule: read with the membership in one request
 * (`membershipQuery`), so this shares the guard's answer instead of three more round trips.
 */
export function useMyRoles() {
  const supabase = useSupabase();
  const { userId, tenantId } = useScope();
  return useQuery({
    ...membershipQuery(supabase, tenantId ?? "", userId ?? ""),
    enabled: userId !== null && tenantId !== null,
    select: (record): MyRoles => ({
      keys: record?.roles.map((role) => role.key) ?? [],
      isPrivileged: record?.roles.some((role) => role.isPrivileged) ?? false,
    }),
  });
}

/** A sign-out action for denied states, shared by the guards. */
export function SignOutButton() {
  const supabase = useSupabase();
  return (
    <Button
      variant="light"
      onClick={() => {
        void supabase.auth.signOut();
      }}
    >
      Sign out
    </Button>
  );
}

export type TenantSettingsRow = Pick<
  Tables<"tenant_settings">,
  "tenant_id" | "require_mfa_for_admins"
>;

/** The tenant's own settings row (readable by members): the admin MFA rule lives here. */
export function tenantSettingsQuery(supabase: AppSupabaseClient, tenantId: string) {
  return queryOptions({
    queryKey: ["tenant-settings", tenantId],
    queryFn: async (): Promise<TenantSettingsRow> => {
      const { data, error } = await supabase
        .from("tenant_settings")
        .select("tenant_id, require_mfa_for_admins")
        .eq("tenant_id", tenantId)
        .single();
      if (error) throw error;
      return data;
    },
  });
}

export function useTenantSettings(): UseQueryResult<TenantSettingsRow> {
  const supabase = useSupabase();
  const { tenantId } = useScope();
  return useQuery({ ...tenantSettingsQuery(supabase, tenantId ?? ""), enabled: tenantId !== null });
}
